import * as React from 'react';
import AmCharts from "@amcharts/amcharts3-react";
import ReactTable from "react-table";
import 'react-table/react-table.css'
import * as moment from 'moment';
import { DateRangePicker, SingleDatePicker, DayPickerRangeController } from 'react-dates';
import { _ } from "@src/services/i18n";
import { classNames, css, mixin, vars, formatters, defaultProps } from '@src/services/theme';
import { Proto, Props, State } from '@src/components/Proto';
import { Ratio, Box } from '@src/components';

// typing

interface LocalProps extends Props {
    getWarnings?: string;
    getErrors?: string;
    getStats?: string;
    getCycles?: string;
}
interface LocalState extends State {
    warnings: any;
    errors: any;
    stats: any;
    cycles: any;
    count?: any;
}

// style
const style = css`
    .stats {
        ${mixin.row}
        > article {
            flex: 1;
        }
    }
    .logs {
        ${mixin.row}
        > div {
            flex: 1;
            .legend-pie {
                font-size: 16px;
                padding: 30px 50px 20px;
                @media screen and (max-width: 1600px) {
                    padding-right: 10px;
                }
                @media screen and (max-width: 768px) {
                    border-top: 1px solid #ccc;
                }
                strong{
                    display: block;
                    font-size: 24px;
                    font-weight: 500;
                    position: relative;
                    &:before{
                        content: "";
                        width: 20px;
                        height: 20px;
                        background-color: #b7e021;
                        border-radius: 10px;
                        top: 3px;
                        left: -27px;
                        position: absolute;
                    }
                    &.arancio{
                        color: #666;
                        &:before{
                        background-color: #ff9900;
                        }
                    }
                    &.giallo{
                        color: #666;
                        &:before{
                        background-color: #ffcc00;
                        }
                    }
                    &.rosso{
                        color: #666;
                        &:before{
                        background-color: #ff0000;
                        }
                    }
                }
            }

            .download {
                display: block;
                width: 60px;
                margin: 0 auto;
                text-align: center;
                color: ${vars.colors.black};
                font-size: 30px;
            }
        }
    }
`;

// component
export class LogsStats extends Proto<LocalProps, LocalState> {
    state = {
        startDate: moment().subtract(14, 'days'),
        // startDate: moment('2018-01-01'),
        endDate: moment()
    } as LocalState;

    async componentWillMount() {
        this.loadWithFilters(this.state);
    }

    async loadAll( filters = {} ) {
        const { getWarnings, getErrors, getStats, getCycles } = this.props;

        const res: any = await this.loadCptData("BatteryLifeStats");
        const resWarnings: any = await this.loadCptData("BatteryLifeStats", { ...filters, url: getWarnings });
        const resErrors: any = await this.loadCptData("BatteryLifeStats", { ...filters, url: getErrors });
        const resStats: any = await this.loadCptData("BatteryLifeStats", { ...filters, url: getStats });
        const resCycles: any = await this.loadCptData("BatteryLifeStats", { ...filters, url: getCycles });
        
        
        if (res.status === 200) {
            this.setState({
                warnings: resWarnings.data.data,
                errors: resErrors.data.data,
                stats: resStats.data.data,
                cycles: resCycles.data.data,
            });
            return Promise.resolve();
        }
    }

    async loadWithFilters( filters ) {
        if (filters.startDate && filters.endDate) {
            await this.loadAll( filters );
        }
        this.setState( filters );
        return Promise.resolve();
    }

    downloadReport( row ) {
        window.open( row.url );
    }

    goToCycle( row ) {
        location.href='/battery/cycle?id=' + row.battid + '&cycleid=' + row.number;
    }

	// render
	render() {
        const loading = this.loading();
        if (loading) return loading;
        const { stats, errors, warnings, cycles } = this.state;
        const { onBattery } = this.props;
        //if (!count) return loading;
        const columns = [
            { Header: _('logsstats_col_number', 'BatteryLife'), accessor: 'number' },
            { Header: _('logsstats_col_type', 'BatteryLife'), accessor: 'type' },
            { Header: _('logsstats_col_message', 'BatteryLife'), accessor: 'message'},
        ];
        const data = (cycles) ? cycles : [];
        
        const tableProps = {
            ...defaultProps.table({
                showPagination: true,
                onClick: (item) => {
//                    if (onBattery) onBattery({id: item.battid});
                    location.href='/battery/cycle?id=' + item.battid + '&cycleid=' + item.number;
                }
            }),
        }

        const columnsErrors = [
            { Header: _('logsstats_col_errors_message', 'BatteryLife'), id: 'message', accessor: v => (_(v.message)) },
            { Header: _('logsstats_col_errors_count', 'BatteryLife'), accessor: 'count'},
        ];
        const dataErrors = (errors) ? errors.map( v => ( {
            message: v.description,
            count: v.count,
            code: v.code
        } )) : [];

        const columnsWarnings = [
            { Header: _('logsstats_col_warnings_message', 'BatteryLife'), id: 'message', accessor: v => (_(v.message)) },
            { Header: _('logsstats_col_warnings_count', 'BatteryLife'), accessor: 'count'},
        ];
        const dataWarnings = (warnings) ? warnings.map( v => ( {
            message: v.description,
            count: v.count,
            code: v.code
        } )) : [];

        const config = {
            "type": "pie",
            "labelsEnabled": false,
            "theme": "light",
            //"titleField": "message",
            "valueField": "count",
            "colorField": "color",
            "outlineAlpha": 0.4,
            "depth3D": 15,
            "angle": 30,
        };
        const dataGraph = (stats) ? [
            { message: _('logsstats_graph_errors', 'BatteryLife'), count: stats.errors, color: vars.colors.red },
            { message: _('logsstats_graph_warnings', 'BatteryLife'), count: stats.warnings, color: vars.colors.orange },
            { message: _('logsstats_graph_ok', 'BatteryLife'), count: stats.correct, color: vars.colors.green },
        ] : [];
        const total = dataGraph.reduce( (acc, v) => (acc+v.count), 0);
        
		const toolbar = () => (
            <DateRangePicker {...this.DatePickerProps()} />
        );
      
		return (
            <Box
                title={_('logsstats_title', 'BatteryLife')}
                toolbar={toolbar}
                collapsed={false}
            >
                <div className={style}>
                    <section className="stats">
                        <article>
                            <div>
                                <ReactTable
                                    {...defaultProps.table()}
                                    data={dataErrors}
                                    columns={columnsErrors}
                                />
                            </div>
                        </article>
                        <article>
                            <div>
                                <ReactTable
                                    {...defaultProps.table()}
                                    data={dataWarnings}
                                    columns={columnsWarnings}
                                />
                            </div>
                        </article>
                    </section>
                    <hr />
                    <section className="logs">
                        <div>
                            <ReactTable
                                {...tableProps}
                                data={data}
                                columns={columns}
                            />
                        </div>
                        <div>
                            {stats &&
                            <div className="legend-pie">
                                <div className="row">
                                    <div className="col-sm-4">
                                        <strong className="rosso">{formatters.percent(stats.errors,total)}%</strong>
                                        {_('logsstats_graph_errors', 'BatteryLife')}
                                    </div>
                                    <div className="col-sm-4">
                                        <strong className="arancio">{formatters.percent(stats.warnings,total)}%</strong>
                                        {_('logsstats_graph_warnings', 'BatteryLife')}
                                    </div>
                                    <div className="col-sm-4">
                                        <strong className="verde">{formatters.percent(stats.correct,total)}%</strong>
                                        {_('logsstats_graph_ok', 'BatteryLife')}
                                    </div>
                                </div>
                            </div>
                            }
                            <Ratio w={16} h={9}>
                                <AmCharts.React style={{ width: "100%", height: "100%" }} options={{...config, dataProvider: dataGraph}} />
                            </Ratio>
                        </div>
                    </section>
                </div>
            </Box>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/BatteryLife/LogsStats.tsx